import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, apiError } from "../context/AuthContext";
import { btn, input, label, selectCls } from "../lib/ui";
import { useI18n } from "../i18n";
import { AuthShell, googleLogin } from "./AuthShell";
import { getStoredRef, captureRefFromUrl, clearRef } from "../lib/ref";

const COUNTRIES = [["ES", "EUR"], ["FR", "EUR"], ["CH", "CHF"], ["PT", "EUR"], ["IT", "EUR"], ["GB", "GBP"]];

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "ES", locale: "es", currency: "EUR" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [accepted, setAccepted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { captureRefFromUrl(); }, []);

  const onCountry = (e) => {
    const c = COUNTRIES.find((x) => x[0] === e.target.value);
    setForm({ ...form, country: c[0], currency: c[1] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const ref = getStoredRef();
      await register({ ...form, ...(ref ? { ref } : {}) });
      clearRef();
      toast.success(t("pub.reg.success"));
      navigate("/login");
    } catch (e) {
      setErr(apiError(e, t("pub.reg.errFallback")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t("pub.reg.title")}
      subtitle={t("pub.reg.subtitle")}
      footer={<>{t("pub.reg.footerPre")}<Link to="/login" className="font-medium text-[#075E62] hover:text-[#0299A0]">{t("pub.reg.footerLink")}</Link></>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <label className={label}>{t("pub.reg.name")}</label>
          <input data-testid="register-name" required value={form.name} onChange={set("name")} className={input} placeholder={t("pub.reg.namePh")} />
        </div>
        <div>
          <label className={label}>{t("pub.reg.email")}</label>
          <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className={input} placeholder={t("pub.reg.emailPh")} />
        </div>
        <div>
          <label className={label}>{t("pub.reg.password")}</label>
          <input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className={input} placeholder="••••••••" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={label}>{t("pub.reg.country")}</label>
            <select data-testid="register-country" value={form.country} onChange={onCountry} className={selectCls}>
              {COUNTRIES.map((c) => <option key={c[0]} value={c[0]}>{t(`pub.country.${c[0]}`)}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>{t("pub.reg.locale")}</label>
            <select data-testid="register-locale" value={form.locale} onChange={set("locale")} className={selectCls}>
              <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option><option value="pt">PT</option><option value="it">IT</option>
            </select>
          </div>
          <div>
            <label className={label}>{t("pub.reg.currency")}</label>
            <select data-testid="register-currency" value={form.currency} onChange={set("currency")} className={selectCls}>
              {["EUR", "CHF", "USD", "GBP", "BRL"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {err && <p data-testid="register-error" className="text-sm text-[#A85751]">{err}</p>}
        <label className="flex items-start gap-2 text-xs text-[#5F8588] leading-relaxed">
          <input type="checkbox" data-testid="register-consent" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="h-4 w-4 mt-0.5 accent-[#35BBAE] shrink-0" />
          <span>{t("pub.reg.consentPre")}<Link to="/privacidad" target="_blank" className="font-medium text-[#0299A0] underline">{t("pub.reg.consentLink")}</Link>{t("pub.reg.consentPost")}</span>
        </label>
        <button type="submit" data-testid="register-submit" disabled={busy || !accepted} className={btn.accent + " w-full"}>{busy ? t("pub.reg.submitting") : t("pub.reg.submit")}</button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[#D5E7E5]" /><span className="text-xs text-[#5F8588]">{t("pub.login.or")}</span><div className="flex-1 h-px bg-[#D5E7E5]" />
      </div>
      <button onClick={googleLogin} data-testid="register-google" className={btn.outline + " w-full"}>{t("pub.login.google")}</button>
    </AuthShell>
  );
}
